import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TabSection } from "../TabSection";
import { SectionHeader } from "../SectionHeader";
import { CompanyCard } from "./CompanyCard";
import { LocationCard } from "./LocationCard";
import { CareHistoryCard } from "./CareHistoryCard";

interface MonumentInfoTabProps {
  memorialId: string;
  monumentCompanyId: string;
}

export const MonumentInfoTab: React.FC<MonumentInfoTabProps> = ({
  memorialId,
  monumentCompanyId,
}) => {
  const { data: company } = useQuery({
    queryKey: ["monumentCompany", monumentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("monument_companies_public")
        .select("name, city, state, zip, website, logo_url, phone, email, street_address, established_year")
        .eq("id", monumentCompanyId)
        .single();
      if (error) throw error;
      // Cast needed until types are regenerated after migration adds phone/email/street_address
      return data as unknown as {
        name: string | null; city: string | null; state: string | null;
        zip: string | null; website: string | null; logo_url: string | null;
        phone: string | null; email: string | null; street_address: string | null;
        established_year: number | null;
      };
    },
    enabled: !!monumentCompanyId,
  });

  const { data: installation } = useQuery({
    queryKey: ["monumentInstallationInfo", memorialId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("monument_installations")
        .select("latitude, longitude, accuracy_category, location_accuracy, captured_at")
        .eq("memorial_id", memorialId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!memorialId,
  });

  const { data: lastServices } = useQuery({
    queryKey: ["careHistory", memorialId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("service_type, delivered_at")
        .eq("memorial_id", memorialId)
        .eq("status", "delivered")
        .order("delivered_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return {
        lastFlowers: data?.find((o) => o.service_type === "flowers"),
        lastCleaning: data?.find((o) => o.service_type === "cleaning"),
      };
    },
    enabled: !!memorialId,
  });

  const hasLocation =
    installation?.latitude != null && installation?.longitude != null;
  const hasCareHistory = !!(lastServices?.lastFlowers || lastServices?.lastCleaning);

  return (
    <TabSection>
      <SectionHeader
        title="Memorial Info"
        subtitle="Details about this memorial and the company behind it"
      />
      <div className="max-w-2xl mx-auto space-y-6">
        {company && <CompanyCard company={company} />}

        {hasLocation && (
          <LocationCard
            installation={{
              latitude: installation!.latitude!,
              longitude: installation!.longitude!,
              accuracy_category: installation!.accuracy_category ?? null,
              location_accuracy: installation!.location_accuracy ?? null,
              captured_at: installation!.captured_at ?? null,
            }}
          />
        )}

        {hasCareHistory && (
          <CareHistoryCard
            lastFlowers={lastServices!.lastFlowers}
            lastCleaning={lastServices!.lastCleaning}
          />
        )}

      </div>
    </TabSection>
  );
};
